import { doDelete, doGet, doPost } from '../http/httpHelper';

export const UNREGISTERED_PATIENT_MESSAGE = 'Usuário não cadastrado no Portal Homecare.';

const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

const isBlankValue = (value) => value === null || value === undefined || String(value).trim() === '';

export const isZeroedValue = (value) => {
    const digits = onlyDigits(value);
    return digits.length > 0 && /^0+$/.test(digits);
};

export const hasRepeatedDigits = (value) => {
    const digits = onlyDigits(value);
    return digits.length > 0 && /^(\d)\1+$/.test(digits);
};

export const hasInvalidDocumentLength = (value, type) => {
    const digits = onlyDigits(value);
    if (digits.length === 0) return false;
    if (type === 'cpf') return digits.length !== 11;
    if (type === 'cnpj') return digits.length !== 14;
    return false;
};

export const displayOrDash = (value, options = {}) => {
    if (isBlankValue(value)) return '-';
    if (options.zeroAsDash && isZeroedValue(value)) return '-';
    if (options.repeatedDigitsAsDash && hasRepeatedDigits(value)) return '-';
    if (hasInvalidDocumentLength(value, options.documentType)) return '-';
    return value;
};

export const formatDocumentOrDash = (value, formatter, options = {}) => {
    const displayValue = displayOrDash(value, {
        zeroAsDash: true,
        repeatedDigitsAsDash: true,
        ...options,
    });
    return displayValue === '-' ? '-' : formatter ? formatter(displayValue) : displayValue;
};

export const isUnregisteredPortalHomecarePatient = (patient) =>
    patient?.isOracleOnly === true ||
    patient?.source === 'oracle_only' ||
    patient?.id === null;

const markPatientRegistrationSource = (patient) => {
    const isOracleOnly = patient?.source === 'oracle_only' || patient?.id === null;

    return {
        ...patient,
        isOracleOnly,
        isPortalHomecareRegistered: !isOracleOnly,
    };
};

export const hasUnregisteredDatalakePatient = (response) => {
    const oracleOnlyCount = Number(response?.oracleDebug?.oracleOnlyCount ?? 0);
    const data = Array.isArray(response?.data) ? response.data : [];

    return oracleOnlyCount > 0 || data.some(isUnregisteredPortalHomecarePatient);
};

export const getPatients = async (cnpj, filter, page, pageSize, status, cache = false) => {
    try {
        const resp = await doGet(
            `/getPatients?page=${page ?? 0}&pageSize=${pageSize ?? 10}${status ? '&status=' + status : ''}${filter !== null && filter !== undefined && filter !== '' ? '&filter=' + filter : ''
            }`,
            {
                cache: cache,
            }
        );
        if (resp.httpStatus !== 200) {
            //handle error
            console.error(resp.error);
            return {
                error: resp.error,
                totalItems: 0,
                data: [],
            };
        }
        resp.body.data = (resp.body.data ?? []).map((x) => ({ ...markPatientRegistrationSource(x), equipments: [] }));
        resp.body.hasUnregisteredDatalakePatient = hasUnregisteredDatalakePatient(resp.body);
        return resp.body;
    } catch (e) {
        return {
            error: e.toString(),
            totalItems: 0,
            data: [],
        };
    }
};

export const getReport = async (cpf, page, pageSize, startDate, endDate) => {
    let url = `/manageReport?`;
    const params = [];
    if (cpf && cpf !== '') {
        params.push('cpf=' + cpf);
    }
    if (startDate && startDate !== '') {
        params.push('startDate=' + startDate);
    }
    if (endDate && endDate !== '') {
        params.push('endDate=' + endDate);
    }
    if (page && page !== '') {
        params.push('page=' + page);
    }
    if (pageSize && pageSize !== '') {
        params.push('pageSize=' + pageSize);
    }
    url = url + params.join('&');
    const resp = await doGet(url);
    return resp;
};

export const savePatient = async (patient) => {
    const resp = await doPost('/patient', patient);
    return resp;
};

export const sendPatientSensitiveChangeRequest = async (payload) => {
    const resp = await doPost('/patient/sensitive-change-request', payload);
    return resp;
};

const getResponseData = (resp) => {
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.body?.data)) return resp.body.data;
    return [];
};

const hasQuotaBalance = (row) => {
    const products = [
        ...(Array.isArray(row?.quotaProducts) ? row.quotaProducts : []),
        ...(Array.isArray(row?.products) ? row.products : []),
    ];

    return products.some((product) => Number(product?.total_balance ?? 0) > 0);
};

const getRechargeBallastBySearchValue = async (cnpj, value) => {
    const searchValue = String(value ?? '').replace(/\D/g, '');
    const cnpjValue = String(cnpj ?? 'null').replace(/\D/g, '') || 'null';

    if (!searchValue) {
        return { rows: 0, data: [] };
    }

    const resp = await doGet(
        `/external/ballast/rechargeList/cnpj/${cnpjValue}/cpf/${encodeURIComponent(searchValue)}/page/0/pageSize/100000`
    );

    if (resp?.httpStatus && resp.httpStatus !== 200) {
        throw resp;
    }

    return resp && typeof resp === 'object' && 'body' in resp ? resp.body : resp;
};

export const getPatientRechargeQuota = async ({ cnpj, sap, cpf } = {}) => {
    const normalizedSap = String(sap ?? '').replace(/\D/g, '');
    const normalizedCpf = String(cpf ?? '').replace(/\D/g, '');

    const primaryResp = await getRechargeBallastBySearchValue(cnpj, normalizedSap || normalizedCpf);
    const primaryData = getResponseData(primaryResp);

    if (primaryData.length > 0 || !normalizedCpf || normalizedCpf === normalizedSap) {
        return {
            rows: primaryResp?.rows ?? primaryData.length,
            data: primaryData,
            hasQuota: primaryData.some(hasQuotaBalance),
        };
    }

    const fallbackResp = await getRechargeBallastBySearchValue(cnpj, normalizedCpf);
    const fallbackData = getResponseData(fallbackResp);

    return {
        rows: fallbackResp?.rows ?? fallbackData.length,
        data: fallbackData,
        hasQuota: fallbackData.some(hasQuotaBalance),
    };
};

export const deactivatePatient = async ({ sap, cpf } = {}) => {
    const params = [];

    if (sap) {
        params.push(`sap=${encodeURIComponent(sap)}`);
    }

    if (cpf) {
        params.push(`cpf=${encodeURIComponent(cpf)}`);
    }

    const resp = await doPost(`/deactivatePatient?${params.join('&')}`);
    return resp;
};

export const getBranch = async (city) => {
    const resp = await doGet(`/branchFromCity?city=${city}`, {
        cache: true,
        expireInSeconds: 3600,
    });
    return resp.body;
};

export const getBranches = async () => {
    const resp = await doGet(`/branches`, {
        cache: true,
        expireInSeconds: 3600,
    });
    return resp.body;
};

export const getDistributionCenterFromSapClient = async (sapClient) => {
    const resp = await doGet(`/distributionCenterFromSapClient?sapClient=${encodeURIComponent(sapClient)}`, {
        cache: true,
        expireInSeconds: 3600,
    });

    return resp && typeof resp === 'object' && 'body' in resp ? resp.body : resp;
};

export const getPatientByCPF = async (cnpj, cpf) => {
    //TODO voltar com o Ativo
    const resp = await getPatients(null, cpf, '0', 1, 'Ativo');
    return resp.data[0];
};

export const getPatientBySap = async (cnpj, sap) => {
    const normalizedSap = String(sap ?? '').replace(/\D/g, '');
    const resp = await getPatients(null, normalizedSap, '0', 10, 'Ativo');
    const data = Array.isArray(resp?.data) ? resp.data : [];

    return data.find((patient) => {
        const patientSap = String(
            patient?.sap_client ??
            patient?.Sap_client ??
            patient?.sap_number ??
            patient?.sap ??
            patient?.sap_customer ??
            patient?.cod_cli ??
            patient?.BP ??
            patient?.receiving_code ??
            ''
        ).replace(/\D/g, '');

        return patientSap === normalizedSap;
    }) ?? data[0];
};

export const getCEP = async (cep) => {
    const resp = await doGet(`/cep?cep=${cep}`, {
        cache: true,
        expireInSeconds: 7200,
    });
    return resp;
};
